import React, { useEffect, useState } from "react";
import axios from "axios";
import HiddenAOReport from "./HiddenAOReport";
import SurveyInviteWidget from "./SurveyInviteWidget";

// ── Recompute the profile code from scores instead of trusting a possibly
//    stale server-stored categoryProfile value ──────────────────────────────
const computeCategoryProfile = (scores) => {
  if (!scores) return "";
  const { AR = 0, TR = 0, UR = 0 } = scores;
  const isHighAR = AR > 12;
  const isHighTR = TR >= 6;
  if (isHighAR && isHighTR) return "AR & TR";
  if (isHighAR) return "AR";
  if (isHighTR && TR >= UR) return "TR";
  return "UR";
};

// ── Category → colour map ─────────────────────────────────────────────────────
const categoryColor = (profile) => {
  if (!profile) return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  if (profile.includes("AR") && profile.includes("TR"))
    return { bg: "#fdf6e3", color: "#d88a1f", border: "#fde68a" }; // AR+TR → gold
  if (profile === "AR")
    return { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" }; // AR   → green
  if (profile === "TR")
    return { bg: "#dbeafe", color: "#3f7cac", border: "#bfdbfe" }; // TR   → blue
  if (profile === "UR")
    return { bg: "#fee2e2", color: "#c2413b", border: "#fecaca" }; // UR   → red
  return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
};

// ── Score pill colour ─────────────────────────────────────────────────────────
const scoreColor = (val, max) => {
  if (val === null || val === undefined) return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  const pct = max > 0 ? val / max : 0;
  if (pct >= 0.6) return { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" }; // high   → green
  if (pct >= 0.35) return { bg: "#dbeafe", color: "#3f7cac", border: "#bfdbfe" }; // mid    → blue
  return             { bg: "#fef3c7", color: "#d88a1f", border: "#fde68a" };      // low    → amber
};

// ── AO dimension display config ───────────────────────────────────────────────
const AO_DIMS = [
  { key: "AR", label: "Achievement Related", title: "Achievement Related" },
  { key: "TR", label: "Task Related",        title: "Task Related"        },
  { key: "UR", label: "Unrelated",           title: "Unrelated"           },
];

// ── Profile code → full-form label ────────────────────────────────────────────
const profileFullForm = (profile) => {
  if (!profile) return "—";
  if (profile.includes("AR") && profile.includes("TR")) return "Achievement Related & Task Related";
  if (profile === "AR") return "Achievement Related";
  if (profile === "TR") return "Task Related";
  if (profile === "UR") return "Unrelated";
  return profile;
};

// ── Profile colour accent for summary pills ───────────────────────────────────
const profileBadgeColor = (profile) => {
  if (!profile) return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  if (profile.includes("AR") && profile.includes("TR"))
    return { bg: "#fdf6e3", color: "#d88a1f", border: "#fde68a" };
  if (profile === "AR") return { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" };
  if (profile === "TR") return { bg: "#dbeafe", color: "#3f7cac", border: "#bfdbfe" };
  if (profile === "UR") return { bg: "#fee2e2", color: "#c2413b", border: "#fecaca" };
  return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
};

export default function GlobalAOResponses({ surveyId }) {
  const [aoData,         setAoData]         = useState([]);
  const [loading,        setLoading]        = useState(true);

  // ── Filters ──
  const [filterName,      setFilterName]      = useState("");
  const [filterEmail,     setFilterEmail]     = useState("");
  const [filterCompany,   setFilterCompany]   = useState("");
  const [filterDept,      setFilterDept]      = useState("");
  const [filterProfile,   setFilterProfile]   = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate,   setFilterEndDate]   = useState("");

  // ── PDF generation state ──
  const [downloadData,    setDownloadData]    = useState(null);
  const [isGeneratingRow, setIsGeneratingRow] = useState(null);

  // ================= FETCH =================
  useEffect(() => {
    if (surveyId) fetchAOResults();
  }, [surveyId]);

  const fetchAOResults = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/response/calculate-score/ao/all`,
        { params: { survey_id: surveyId } }
      );
      const rows = res.data.data || [];
      setAoData(rows.map(u => ({
        ...u,
        categoryProfile: computeCategoryProfile(u.scores) || u.categoryProfile,
      })));
    } catch (err) {
      console.error("❌ Failed to fetch AO results", err);
    } finally {
      setLoading(false);
    }
  };

  // ================= FILTER =================
  const uniqueCompanies = [
    ...new Set(aoData.map((u) => (u?.company || "").trim()).filter(Boolean)),
  ].sort();

  const uniqueDepts = [
    ...new Set(aoData.map((u) => (u?.department || "").trim()).filter(Boolean)),
  ].sort();

  const uniqueProfiles = [
    ...new Set(aoData.map((u) => u?.categoryProfile).filter(Boolean)),
  ].sort();

  const filteredData = aoData.filter((user) => {
    const name    = (user?.name    || "").toLowerCase();
    const email   = (user?.email   || "").toLowerCase();
    const company = (user?.company || "").trim().toLowerCase();
    const dept    = (user?.department || "").trim().toLowerCase();
    const profile = user?.categoryProfile || "";

    const matchName    = name.includes(filterName.toLowerCase());
    const matchEmail   = email.includes(filterEmail.toLowerCase());
    const matchCompany = filterCompany === "" || company === filterCompany.toLowerCase();
    const matchDept    = filterDept    === "" || dept    === filterDept.toLowerCase();
    const matchProfile = filterProfile === "" || profile === filterProfile;

    let matchDate = true;
    if (filterStartDate || filterEndDate) {
      const submissionDate = new Date(user.submission_time);
      submissionDate.setHours(0, 0, 0, 0);

      if (filterStartDate) {
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        if (submissionDate < start) matchDate = false;
      }
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);
        if (submissionDate > end) matchDate = false;
      }
    }

    return matchName && matchEmail && matchCompany && matchDept && matchProfile && matchDate;
  });

  const clearFilters = () => {
    setFilterName("");
    setFilterEmail("");
    setFilterCompany("");
    setFilterDept("");
    setFilterProfile("");
    setFilterStartDate("");
    setFilterEndDate("");
  };

  // ── Summary counts per profile ──
  const profileCounts = aoData.reduce((acc, u) => {
    const p = u?.categoryProfile;
    if (p) acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {});

  // ================= PDF DOWNLOAD =================
  const handleDownloadPDF = (user, rowIndex) => {
    setIsGeneratingRow(rowIndex);

    setDownloadData({
      userInfo: {
        name:  user?.name  || "Participant",
        email: user?.email || "",
        phone: user?.phone || "",
      },
      scores:          user.scores          || { AR: 0, TR: 0, UR: 0 },
      categoryProfile: user.categoryProfile || "—",
      insightText:     buildInsight(user.scores),
      submission_time: user.submission_time,
    });
  };

  function getCategoryLabel(scores) {
  if (!scores) return "—";
  const { AR = 0, TR = 0, UR = 0 } = scores;
  const isHighAR = AR > 12;
  const isHighTR = TR >= 6;
  if (isHighAR && isHighTR) return "High AR & TR";
  if (isHighAR) return "High AR";
  if (isHighTR && TR >= UR) return "High TR";  // also applies the UR fix from earlier
  return "UR";
}

function buildInsight(sc) {
  if (!sc) return "";
  const label = getCategoryLabel(sc);
  const total = (sc.AR ?? 0) + (sc.TR ?? 0) + (sc.UR ?? 0);
  const arPct = total > 0 ? Math.round(((sc.AR ?? 0) / total) * 100) : 0;

  if (label === "High AR & TR")
    return "You score highly on both Achievement Related and Task Related dimensions — an excellent profile. You act as per the requirements at the workplace, combining reliability with achievement drive and progressing naturally from structured execution to independent goal-setting.";
  if (label === "High AR")
    return `Your dominant orientation is Achievement Related (${arPct}% of responses). You have a high level of achievement motivation — you set your own goals, thrive on challenges, and are highly reliable. Freedom to work in your own style is what drives your best performance.`;
  if (label === "High TR")
    return "Your dominant orientation is Task Related. You are highly reliable and ensure work is completed within timelines under any circumstances. To grow further, seek challenges beyond assigned targets and progressively expand your independence.";
  return "Your profile falls in the Unrelated to Achievement zone. This reflects a comfort-zone orientation — a cause for awareness and conscious action. The path forward begins with small challenges and a deliberate shift toward self-directed goals.";
}

  const handleDownloadComplete = () => {
    setDownloadData(null);
    setIsGeneratingRow(null);
  };

  // ================= UI =================
  if (loading) return <div className="loading-state">Loading AO data...</div>;

  return (
    <div style={{ marginTop: "20px" }}>
       <SurveyInviteWidget surveyId={surveyId} />

      {/* Hidden PDF renderer */}
      <HiddenAOReport
        participantData={downloadData}
        onComplete={handleDownloadComplete}
      />

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
        <h3 style={{ margin: 0 }}>🎯 AO Achievement Orientation ({filteredData.length})</h3>
        <span style={{
          background: "#A80D2C", color: "white",
          padding: "5px 10px", borderRadius: "4px",
          fontSize: "12px", fontWeight: "bold",
        }}>
          Global Public View
        </span>
      </div>

      {/* ── Profile summary pills (clickable to filter) ── */}
      {aoData.length > 0 && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: "8px",
          marginBottom: "16px",
        }}>
          {Object.entries(profileCounts).map(([profileCode, count]) => {
            const pc    = profileBadgeColor(profileCode);
            const label = profileFullForm(profileCode);
            return (
              <span
                key={profileCode}
                onClick={() => setFilterProfile(filterProfile === profileCode ? "" : profileCode)}
                title="Click to filter by this profile"
                style={{
                  background:    pc.bg,
                  color:         pc.color,
                  border:        `1px solid ${pc.border}`,
                  padding:       "4px 12px",
                  borderRadius:  "999px",
                  fontSize:      "12px",
                  fontWeight:    "700",
                  cursor:        "pointer",
                  outline:       filterProfile === profileCode ? `2px solid ${pc.color}` : "none",
                  outlineOffset: "2px",
                }}
              >
                {label}: {count}
              </span>
            );
          })}
          <span style={{ fontSize: "12px", color: "#888", alignSelf: "center", marginLeft: "4px" }}>
            ← click to filter
          </span>
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "10px",
        background: "#f8f9fa", padding: "15px", borderRadius: "8px",
        border: "1px solid #ddd", marginBottom: "20px", alignItems: "center",
      }}>
        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Name</label>
          <input
            type="text"
            placeholder="Search name..."
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Email</label>
          <input
            type="text"
            placeholder="Search email..."
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Company</label>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Companies</option>
            {uniqueCompanies.map(c => (
              <option key={c} value={c.toLowerCase()}>{c}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Department</label>
          <select
            value={filterDept}
            onChange={(e) => setFilterDept(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Departments</option>
            {uniqueDepts.map(d => (
              <option key={d} value={d.toLowerCase()}>{d}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Profile</label>
          <select
            value={filterProfile}
            onChange={(e) => setFilterProfile(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Profiles</option>
            {uniqueProfiles.map((p) => (
              <option key={p} value={p}>
                {profileFullForm(p)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "140px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>From Date</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "140px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>To Date</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ marginTop: "20px" }}>
          <button
            onClick={clearFilters}
            style={{
              padding: "8px 15px", background: "#A81026", color: "white",
              border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold",
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {aoData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>No AO responses found.</div>
      ) : filteredData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>No responses match your current filters.</div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "8px", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: "12px" }}>Date</th>
                <th style={{ padding: "12px" }}>Participant</th>
                <th style={{ padding: "12px" }}>Company / Dept</th>
                <th style={{ padding: "12px", minWidth: "220px" }}>
                  AO Scores
                </th>
                <th style={{ padding: "12px", minWidth: "130px", background: "#fdf6e3" }}>
                  🎯 Profile
                </th>
                <th style={{ padding: "12px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((user, index) => {
                const scores       = user.scores || {};
                const maxScore     = Math.max(scores.AR ?? 0, scores.TR ?? 0, scores.UR ?? 0);
                const isGenerating = isGeneratingRow === index;
                const profileC     = categoryColor(user.categoryProfile);

                return (
                  <tr key={index} style={{ borderBottom: "1px solid #eee" }}>

                    {/* ── Date ── */}
                    <td style={{ padding: "12px", whiteSpace: "nowrap" }}>
                      {user.submission_time
                        ? <>
                            {new Date(user.submission_time).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", year: "numeric",
                            })}<br />
                            <span style={{ fontSize: "12px", color: "#888" }}>
                              {new Date(user.submission_time).toLocaleTimeString("en-US", {
                                hour: "2-digit", minute: "2-digit",
                              })}
                            </span>
                          </>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      }
                    </td>

                    {/* ── Participant ── */}
                    <td style={{ padding: "12px" }}>
                      <b>{user?.name || "Unknown"}</b><br />
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        {user?.email}<br />
                        {user?.phone || ""}
                      </span>
                    </td>

                    {/* ── Company / Dept ── */}
                    <td style={{ padding: "12px" }}>
                      {user?.company
                        ? <><b>{user.company}</b><br /></>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>N/A</span>
                      }
                      {user?.department
                        ? <span style={{ fontSize: "13px", color: "#555" }}>{user.department}</span>
                        : null
                      }
                    </td>

                    {/* ── AO Score pills ── */}
                    <td style={{ padding: "12px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {AO_DIMS.map(({ key, label, title }) => {
                          const val    = scores[key] ?? null;
                          const c      = scoreColor(val, maxScore);
                          const isTop  = val === maxScore && maxScore > 0;
                          return (
                            <span
                              key={key}
                              title={title}
                              style={{
                                background:   c.bg,
                                color:        c.color,
                                border:       `${isTop ? "2px" : "1px"} solid ${c.border}`,
                                padding:      "2px 8px",
                                borderRadius: "4px",
                                fontSize:     "12px",
                                fontWeight:   isTop ? "800" : "600",
                                whiteSpace:   "nowrap",
                              }}
                            >
                              {label}: {val ?? "—"}
                            </span>
                          );
                        })}
                      </div>
                    </td>

                    {/* ── Category Profile ── */}
                    <td style={{ padding: "12px", background: "#fffdf7" }}>
                      <span
                        style={{
                          background:   profileC.bg,
                          color:        profileC.color,
                          border:       `2px solid ${profileC.border}`,
                          padding:      "3px 10px",
                          borderRadius: "6px",
                          fontSize:     "13px",
                          fontWeight:   "800",
                          whiteSpace:   "nowrap",
                        }}
                      >
                        {profileFullForm(user.categoryProfile) || "—"}
                      </span>
                    </td>

                    {/* ── Download ── */}
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <button
                        onClick={() => handleDownloadPDF(user, index)}
                        disabled={isGenerating}
                        style={{
                          background:   isGenerating ? "#95a5a6" : "#A81026",
                          color:        "white",
                          border:       "none",
                          padding:      "8px 12px",
                          borderRadius: "6px",
                          cursor:       isGenerating ? "not-allowed" : "pointer",
                          fontWeight:   "bold",
                          fontSize:     "12px",
                          display:      "flex",
                          alignItems:   "center",
                          gap:          "5px",
                          margin:       "0 auto",
                          transition:   "background 0.3s",
                          whiteSpace:   "nowrap",
                        }}
                      >
                        {isGenerating ? "⏳ Generating..." : "📄 Download"}
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}